import { useMemo } from 'react';
import { OrderFormData, OrderPricing, PricingItem, isBidditOrPublicSale } from '@/types/order';

// === PRIX DES PACKS ===
const PRICES = {
  visites: 699,
  marketing_bien: 249,
  marketing_terrain: 149,
  marketing_video_or_360: 99,
  marketing_drone: 149,
  certificat_peb: 279,
  controle_elec: 149,
  supplement_visite: 79,
};

const INCLUDED_WEEKS = 4;

/**
 * Hook pour calculer le pricing en temps réel
 */
export function useOrderPricing(data: OrderFormData): OrderPricing {
  return useMemo(() => {
    const items: PricingItem[] = [];
    let subtotal = 0;
    let supplementVisites = 0;
    let extraVisitesCount = 0;
    let hasQuoteItems = false;

    // Pack Visites
    if (data.packVisites) {
      items.push({
        label: 'Pack Visites (2 mois, panneau inclus)',
        price: PRICES.visites,
      });
      subtotal += PRICES.visites;
    }

    // Pack Marketing
    if (data.packMarketing) {
      if (data.marketingType === 'terrain') {
        items.push({
          label: 'Pack Marketing — Terrain (drone inclus)',
          price: PRICES.marketing_terrain,
        });
        subtotal += PRICES.marketing_terrain;
      } else {
        items.push({
          label: 'Pack Marketing — Bien immobilier',
          price: PRICES.marketing_bien,
        });
        subtotal += PRICES.marketing_bien;

        // Upgrades (uniquement pour bien)
        if (data.marketingUpgrade === 'video') {
          items.push({ label: 'Vidéo immobilière', price: PRICES.marketing_video_or_360 });
          subtotal += PRICES.marketing_video_or_360;
        } else if (data.marketingUpgrade === '360') {
          items.push({ label: 'Visite virtuelle 360°', price: PRICES.marketing_video_or_360 });
          subtotal += PRICES.marketing_video_or_360;
        }

        if (data.marketingDrone) {
          items.push({ label: 'Photos drone', price: PRICES.marketing_drone });
          subtotal += PRICES.marketing_drone;
        }
      }
    }

    // Pack Contrôles
    if (data.packControles.includes('peb')) {
      items.push({ label: 'Certificat PEB', price: PRICES.certificat_peb });
      subtotal += PRICES.certificat_peb;
    }
    if (data.packControles.includes('elec')) {
      items.push({ label: 'Contrôle électricité', price: PRICES.controle_elec });
      subtotal += PRICES.controle_elec;
    }

    // Calcul des visites supplémentaires (Biddit/Publique + Pack Visites)
    if (data.packVisites && isBidditOrPublicSale(data.typeVente) && data.deadline && data.slotDateDebut) {
      const extraVisits = calculateExtraVisits(
        data.slotCreneaux,
        data.deadline,
        data.slotDateDebut
      );
      extraVisitesCount = extraVisits;
      supplementVisites = extraVisits * PRICES.supplement_visite;
    }

    const total = subtotal + supplementVisites;

    return {
      items,
      subtotal,
      supplementVisites,
      extraVisitesCount,
      discount: 0,
      discountPercent: 0,
      hasComboDiscount: false,
      total,
      hasQuoteItems,
    };
  }, [data]);
}

/**
 * Calcule le nombre de visites supplémentaires hors forfait
 */
function calculateExtraVisits(
  slots: { jour: string; heureDebut: string }[],
  deadline: string,
  startDate: string
): number {
  if (!deadline || !startDate || slots.length === 0) {
    return 0;
  }

  try {
    const deadlineDate = new Date(deadline);
    const start = new Date(startDate);

    const includedStartDate = new Date(deadlineDate);
    includedStartDate.setDate(includedStartDate.getDate() - INCLUDED_WEEKS * 7);

    if (start >= includedStartDate) {
      return 0;
    }

    const weeksBeforeIncluded = Math.ceil(
      (includedStartDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 7)
    );

    return weeksBeforeIncluded * slots.length;
  } catch (error) {
    console.error('Error calculating extra visits:', error);
    return 0;
  }
}

/**
 * Helper pour formater un prix
 */
export function formatPrice(price: number): string {
  return `${price.toFixed(2)} €`;
}

/**
 * Helper pour vérifier si urgence (deadline < 14 jours)
 */
export function isUrgent(deadline: string): boolean {
  if (!deadline) return false;
  try {
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffDays = Math.ceil((deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays < 14;
  } catch {
    return false;
  }
}
